import React, { useState, useEffect } from 'react';
import './AdminUpload.css';
import UserSearchInput from '../components/UserSearchInput';
import GroupManager from '../components/GroupManager';
import { IconGear, IconUsers, IconShare, IconChart, IconBuilding, IconTrash, IconDoc } from '../components/Icons';

// Seção reutilizável de gestão de acesso (admins ou compartilhadores)
function AccessSection({ title, icon, description, endpoints, currentUserEmail, protectSelf }) {
  const [rows, setRows] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(endpoints.list);
      const data = await res.json();
      if (!data.error) setRows(data[endpoints.listKey] || []);
    } catch (err) {
      console.error(`Erro ao carregar ${title}:`, err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setMessage({ error: 'Email inválido' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(endpoints.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail })
      });
      const data = await res.json();

      if (data.success) {
        setMessage({ success: `${newEmail} adicionado` });
        setNewEmail('');
        load();
      } else {
        setMessage({ error: data.error });
      }
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remover ${email}?`)) return;

    try {
      const res = await fetch(`${endpoints.remove}?email=${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });
      const data = await res.json();

      if (data.success) {
        setMessage({ success: `${email} removido` });
        load();
      } else {
        setMessage({ error: data.error });
      }
    } catch (err) {
      setMessage({ error: err.message });
    }
  };

  return (
    <div className="upload-form" style={{ marginTop: 24 }}>
      <h3 className="section-title-admin"><span className="section-icon">{icon}</span>{title}</h3>
      {description && <p className="section-description">{description}</p>}

      <div className="form-group">
        <label>Adicionar novo</label>
        <div className="add-admin-row">
          <UserSearchInput
            value={newEmail}
            onChange={setNewEmail}
            placeholder="Buscar por nome ou e-mail…"
            disabled={loading}
          />
          <button className="add-admin-button" onClick={handleAdd} disabled={loading}>
            {loading ? '...' : '+ Adicionar'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`upload-result ${message.success ? 'success' : 'error'}`}>
          <p>{message.success || message.error}</p>
        </div>
      )}

      <div className="admins-list">
        <label>Cadastrados ({rows.length})</label>
        {rows.length === 0 ? (
          <p className="empty-admins">Nenhum registro cadastrado.</p>
        ) : (
          <ul>
            {rows.map((row, i) => (
              <li key={i} className="admin-item">
                <div>
                  <span className="admin-email">{row.email}</span>
                  <span className="admin-meta">
                    Por {row.addedBy} em {row.addedAt ? new Date(row.addedAt).toLocaleDateString('pt-BR') : '—'}
                  </span>
                </div>
                <button
                  className="remove-admin-button"
                  onClick={() => handleRemove(row.email)}
                  disabled={protectSelf && row.email === currentUserEmail}
                  aria-label={`Remover ${row.email}`}
                >
                  <IconTrash size={17} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const CONTAINER_LABELS = {
  'docs': 'Pessoal',
  'docs-publicos': 'Públicos',
  'docs-diretoria': 'Diretoria',
  'docs-gerenciais': 'Gerenciais (Regional)',
  'docs-admcen': 'ADMCEN (Corporativo)'
};
const labelContainer = (c) => CONTAINER_LABELS[c] || c || '—';

// Lixeira (CR FASE 3 / E2): documentos excluídos por admins/compartilhadores
// ficam no container 'docs-excluidos'. Só o administrador vê aqui e pode
// RESTAURAR (volta à origem) ou EXCLUIR DEFINITIVAMENTE (apaga do storage).
function TrashSection() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [message, setMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/listDeleted');
      const data = await res.json();
      if (data.error) { setMessage({ error: data.error }); setFiles([]); }
      else setFiles(data.files || []);
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtDate = (s) => (s ? new Date(s).toLocaleString('pt-BR') : '—');

  const handleRestore = async (f) => {
    if (!window.confirm(`Restaurar "${f.name}" para ${labelContainer(f.originContainer)}?`)) return;
    setBusyKey(f.key);
    setMessage(null);
    try {
      const res = await fetch('/api/restoreDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: f.key })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ success: `"${f.name}" restaurado para ${labelContainer(f.originContainer)}` });
        setFiles((prev) => prev.filter((x) => x.key !== f.key));
      } else {
        setMessage({ error: data.error || 'Falha ao restaurar' });
      }
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setBusyKey(null);
    }
  };

  const handlePurge = async (f) => {
    if (!window.confirm(`Excluir DEFINITIVAMENTE "${f.name}"?\n\nEsta ação apaga o arquivo do armazenamento e NÃO pode ser desfeita.`)) return;
    setBusyKey(f.key);
    setMessage(null);
    try {
      const res = await fetch(`/api/purgeDocument?key=${encodeURIComponent(f.key)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setMessage({ success: `"${f.name}" excluído definitivamente` });
        setFiles((prev) => prev.filter((x) => x.key !== f.key));
      } else {
        setMessage({ error: data.error || 'Falha ao excluir' });
      }
    } catch (err) {
      setMessage({ error: err.message });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="upload-form" style={{ marginTop: 24 }}>
      <h3 className="section-title-admin"><span className="section-icon"><IconTrash /></span>Lixeira</h3>
      <p className="section-description">
        Documentos excluídos vão para cá. Só administradores podem restaurá-los ou excluí-los definitivamente do armazenamento.
      </p>

      {message && (
        <div className={`upload-result ${message.success ? 'success' : 'error'}`}>
          <p>{message.success || message.error}</p>
        </div>
      )}

      <div className="admins-list">
        <label>Excluídos ({files.length})</label>
        {loading ? (
          <p className="empty-admins">Carregando lixeira...</p>
        ) : files.length === 0 ? (
          <p className="empty-admins">A lixeira está vazia.</p>
        ) : (
          <ul>
            {files.map((f) => (
              <li key={f.key} className="admin-item">
                <div>
                  <span className="admin-email" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <IconDoc size={15} /> {f.name}
                  </span>
                  <span className="admin-meta">
                    {labelContainer(f.originContainer)}
                    {f.deletedBy ? ` • excluído por ${f.deletedBy}` : ''}
                    {f.deletedAt ? ` em ${fmtDate(f.deletedAt)}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    className="add-admin-button"
                    onClick={() => handleRestore(f)}
                    disabled={busyKey === f.key}
                  >
                    {busyKey === f.key ? '...' : 'Restaurar'}
                  </button>
                  <button
                    className="remove-admin-button"
                    onClick={() => handlePurge(f)}
                    disabled={busyKey === f.key}
                    title="Excluir definitivamente"
                    aria-label={`Excluir definitivamente ${f.name}`}
                  >
                    <IconTrash size={17} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AdminAccess({ user }) {
  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2><span className="section-icon"><IconGear /></span>Gestão de Acessos</h2>
        <p>Administre quem tem acesso de administrador e de compartilhador</p>
      </div>

      <AccessSection
        title="Administradores"
        icon={<IconUsers />}
        description="Administradores gerenciam acessos e podem enviar documentos."
        currentUserEmail={user.userDetails}
        protectSelf={true}
        endpoints={{
          list: '/api/listAdmins',
          listKey: 'admins',
          add: '/api/addAdmin',
          remove: '/api/removeAdmin'
        }}
      />

      <AccessSection
        title="Compartilhadores"
        icon={<IconShare />}
        description="Compartilhadores podem enviar documentos, mas não gerenciam acessos."
        currentUserEmail={user.userDetails}
        protectSelf={false}
        endpoints={{
          list: '/api/listUploaders',
          listKey: 'uploaders',
          add: '/api/addUploader',
          remove: '/api/removeUploader'
        }}
      />

      <AccessSection
        title="DECON (Análise DRE)"
        icon={<IconChart />}
        description="Acesso à visão consolidada do DRE (somente leitura). A diretoria já tem acesso por padrão."
        currentUserEmail={user.userDetails}
        protectSelf={false}
        endpoints={{
          list: '/api/listDecon',
          listKey: 'decon',
          add: '/api/addDecon',
          remove: '/api/removeDecon'
        }}
      />

      <GroupManager
        title="Grupo Regional"
        icon={<IconChart />}
        description="Acesso aos documentos gerenciais da regional. Entra por cargo (gerente regional e comerciais lotados em regional) — inclua manualmente para cobrir exceções/férias."
        group="regional"
      />

      <GroupManager
        title="Grupo ADMCEN (Corporativo)"
        icon={<IconBuilding />}
        description="Acesso aos documentos corporativos. Entra por cargo (diretoria, gerentes de departamento e comerciais lotados em Compras) — inclua manualmente para cobrir exceções."
        group="admcen"
      />

      <TrashSection />
    </div>
  );
}

export default AdminAccess;
